/** @format */

import {
  AddToCartParams,
  AtcMessage,
  Chain,
  HeaderStoreSelection,
  PersonalizationData,
} from '@shared/data-access/interfaces';
import { Injectable, inject } from '@angular/core';

import { CartAnalyticsService } from './cart.analytics.service';
import { CartDao } from './cart.dao';
import { CartEngineDao } from './cart-engine.dao';
import { CookieService } from '../cookie.service';
import { DeviceService } from '../device.service';
import { HttpParams } from '@angular/common/http';
import { LocalStorageService } from '../local-storage.service';
import { Observable } from 'rxjs';
import { PersonalizedLineAidTextString } from '@shared/data-access/enums';
import { Store } from '../app.store';
import { WINDOW } from '../window/window.service';

@Injectable({
  providedIn: 'root',
})
export class CartService {
  private readonly analyticsService = inject(CartAnalyticsService);
  private readonly cartDao = inject(CartDao);
  private readonly chain = inject(Chain);
  private readonly cookieService = inject(CookieService);
  private readonly deviceService = inject(DeviceService);
  private readonly localStorage = inject(LocalStorageService);
  private readonly store = inject(Store);
  protected readonly window = inject(WINDOW) as Window;
  private readonly cartEngineDao = inject(CartEngineDao);

  // constant
  public readonly ANALYTICS_ATC_ERROR_ID = 'DCSG_NGX_PDP_ATC_ERROR';
  public readonly ANALYTICS_ADDON_ATC_ERROR_ID = 'DCSG_NGX_PDP_ADDON_POST_ERROR';
  public readonly ANALYTICS_ATC_ERROR_MESSAGE_PREFIX = 'PDP:';

  // component level variables
  public storeId = this.chain.storeId;
  protected chainIdentifierAbbr = this.chain.chainIdentifierAbbr;

  /**
   * @description
   * Initializes the Cart Service.  Does any data setup as necessary.
   */
  public initialize(): void {
    // Current cart count
    let cartCount: string;

    /*
     * Use the NGX cookie as the primary data, and override from the legacy cookie where necessary
     */
    // Check to see if NGX cart count cookie is present
    if (this.cookieService.check('DCSG_NGX_CART_COUNT')) {
      // Set the Cart count
      cartCount = this.cookieService.get('DCSG_NGX_CART_COUNT');
    }

    // Check to see if DSG or GG mobile / tablet
    if (
      (this.chain.serviceIdentifierAbbr === 'dsg' || this.chain.serviceIdentifierAbbr === 'gg') &&
      !this.deviceService.isDesktop()
    ) {
      // Check to see if legacy cartCount cookie is present for DSG mobile / tablet
      if (this.cookieService.check('cartCount')) {
        // Set the Cart count
        cartCount = this.cookieService.get('cartCount');
      }
      // Check to see if DSG or GG desktop
    } else if (
      (this.chain.serviceIdentifierAbbr === 'dsg' || this.chain.serviceIdentifierAbbr === 'gg') &&
      this.cookieService.check('DSG_CartQTY')
    ) {
      // Set the Cart count
      cartCount = this.cookieService.get('DSG_CartQTY');
    }

    // Check to see if there is anything to set
    if (
      typeof cartCount !== 'undefined' &&
      typeof cartCount === 'string' &&
      cartCount.trim() !== '' &&
      cartCount.length > 0
    ) {
      // Set the Cart count on the Store
      this.store.set('CART_COUNT', isNaN(parseInt(cartCount, 10)) ? 0 : parseInt(cartCount, 10));
    }

    // Listen for updates to CART_COUNT to update appropriate cookies
    this.store.select('CART_COUNT').subscribe((updatedCount: number) => {
      // Set the cart cookies
      this.setCartCookies(updatedCount);
    });
  }

  public addSaveForLaterV2SkuToCart$(item, retries = 1): Observable<any> {
    let personalizedItemData: PersonalizationData = null;
    let customizedItemData = null;
    if (item.productAttributes?.personalizedAttribute !== undefined) {
      personalizedItemData = {
        hasLineAid: false,
        hasNumberLine: false,
        hasTextLine: false,
        x_personalizedAttr: true,
        x_personalizationLineStyle: 'none',
      } as PersonalizationData;

      // remove timestamp from personalized skus
      item.sku = item.sku.substring(0, item.sku.indexOf('-'));

      item.productAttributes.personalizedAttribute.forEach((attribute) => {
        if (attribute.title.toLowerCase().includes('text line')) {
          personalizedItemData.hasTextLine = true;
          if (attribute.title.toLowerCase().includes('1')) {
            personalizedItemData.x_personalizationTextLine1 = attribute.value.toUpperCase();
          } else if (attribute.title.toLowerCase().includes('2')) {
            personalizedItemData.x_personalizationTextLine2 = attribute.value.toUpperCase();
          } else if (attribute.title.toLowerCase().includes('3')) {
            personalizedItemData.x_personalizationTextLine3 = attribute.value.toUpperCase();
          }
        }

        if (attribute.title.toLowerCase().includes('line style')) {
          personalizedItemData.hasLineAid = true;
          personalizedItemData.x_personalizationLineStyle = attribute.value;
        }

        if (attribute.title.toLowerCase().includes('number')) {
          personalizedItemData.hasNumberLine = true;
          personalizedItemData.x_personalizationNumberLine1 = attribute.value;
          personalizedItemData.numberLineCharLimit = attribute.value.length;
        }
      });
    }

    if (item.productAttributes?.customizedAttribute !== undefined) {
      customizedItemData = {
        MyCustomizerMap: {
          designId: item.sku.substr(item.sku.indexOf('MC-') + 3),
        },
      };
    }

    return this.cartDao.addSkuToCart$(
      item.sku,
      1,
      null,
      personalizedItemData,
      customizedItemData,
      retries,
    );
  }

  /**
   * Returns an AtcMessage for displaying
   * @param result
   * @param sku
   * @param message
   * @param qty
   * @param isBopisSelected
   * @param store
   */
  public generateSuccessMessage(
    result: string,
    skuId: string,
    message: string,
    qty: number | string,
    isBopisSelected: boolean,
    store: string,
    productEcode: string,
    productTitle: string,
    callOrigin: string,
    warrantyEcode?: string,
  ): AtcMessage {
    return {
      callOrigin,
      success: result,
      skuId,
      quantity: qty,
      chain: this.chainIdentifierAbbr,
      eCode: productEcode,
      errorMessage: message,
      bopisSelected: isBopisSelected,
      browser: this.window.navigator.userAgent,
      bopisStore: store,
      title: productTitle,
      dcsgNgxPageUrl: this.window.location,
      deviceInfo: this.deviceService.getDeviceInfo(),
      swimlane: this.window?._dsgTag?.customerLane,
      logType: 'ATC',
      ...(warrantyEcode ? { warrantyEcode } : {}),
    };
  }

  /**
   * Fires analytics Event for ATC error
   */
  public fireATCErrorAnalyticEvent(error: any, isAddOn = false) {
    // Error message
    const errorMessage = `${this.ANALYTICS_ATC_ERROR_MESSAGE_PREFIX}${error.message}`;

    // Set error ID
    this.analyticsService.setErrorId(this.ANALYTICS_ATC_ERROR_ID);

    // Set error message
    this.analyticsService.setErrorMessage(errorMessage);

    // Trigger Analytics error
    const errorId = isAddOn ? this.ANALYTICS_ADDON_ATC_ERROR_ID : this.ANALYTICS_ATC_ERROR_ID;
    this.analyticsService.triggerAnalyticsEvent(errorId, {
      ErrorMessage: errorMessage,
    });
  }

  // TODO: Check the expires logic!
  /**
   * @description
   * Sets the cart cookies
   */
  private setCartCookies(count: number): void {
    // Don't set a cookie value of 0 since it's the default value
    if (count > 0) {
      // Update the cookie
      this.cookieService.set('DCSG_NGX_CART_COUNT', `${count}`, 30, '/');

      // Check to see if DSG or GG and mobile
      if (
        (this.chain.serviceIdentifierAbbr === 'dsg' || this.chain.serviceIdentifierAbbr === 'gg') &&
        !this.deviceService.isDesktop()
      ) {
        // Check to see if legacy cartCount cookie is present for DSG mobile / tablet
        this.cookieService.set('cartCount', `${count}`, 30, '/');
        // Check to see if DSG or gg
      } else if (
        this.chain.serviceIdentifierAbbr === 'dsg' ||
        this.chain.serviceIdentifierAbbr === 'gg'
      ) {
        // Check to see if legacy cartCount cookie is present for DSG desktop
        this.cookieService.set('DSG_CartQTY', `${count}`, 30, '/');
      } else {
        // Update localStorage for F&S
        this.localStorage.set('miniCartCount', `${count}`);
      }
    } else {
      // We need to remove the miniCartCount from local storage if the count is 0
      this.localStorage.remove('miniCartCount');
    }
  }

  addAddOnToExistingCartProduct$(
    warrantySku: number,
    parentSku: string,
    qty: number,
    type: string,
  ) {
    return this.cartEngineDao.postContentsSkuAddOn$(warrantySku, parentSku, qty, type);
  }

  addScorecardPlusToCart$(scorecardPlusSku: string): Observable<number> {
    return this.cartEngineDao.putContentsSku$(scorecardPlusSku, 'v2', '?qty=1&nonMerch=true');
  }

  /**
   * @description
   * Main add to cart method that orchestrates building params and calling the DAO
   * This method contains the core business logic for adding products to cart
   */
  public addToCart(params: AddToCartParams): Observable<string> {
    const {
      userInputQuantity,
      fulfillmentOption,
      storeInfo,
      zipCode,
      customizationData,
      selectedPersonalizationOptions,
      resolvedSkuPartNumber,
      selectedWarrantyDetails,
    } = params;

    // Convert quantity once for reuse
    const qty = `${userInputQuantity}`;

    // HTTP Params
    let httpParams = new HttpParams().set('qty', qty);

    httpParams = this.setFulfillmentOption(storeInfo, zipCode, fulfillmentOption, httpParams);

    const personalizedData = selectedPersonalizationOptions?.personalization;

    if (typeof personalizedData?.x_personalizedAttr !== 'undefined') {
      httpParams = this.setPersonalizationData(personalizedData, httpParams);
    }

    // Set custom id for custom products
    if (customizationData?.designId) {
      httpParams = httpParams.set('mycustomizer', `${customizationData.designId}`);
    }

    let httpBody;

    const includeWarrantyInBody = !!selectedWarrantyDetails?.planSku;

    if (includeWarrantyInBody) {
      httpBody = {
        addons: [
          {
            sku: selectedWarrantyDetails.planSku,
            qty,
            type: 'warranty',
          },
        ],
      };
    }

    return this.cartDao.addProductToCart$(resolvedSkuPartNumber, httpParams, httpBody);
  }

  /**
   * @description
   * Sets fulfillment option params (BOPIS, BOPL, SDD) on the HTTP params
   */
  public setFulfillmentOption(
    storeInfo: HeaderStoreSelection,
    zipCode: string,
    fulfillmentOption: string,
    httpParams: HttpParams,
  ): HttpParams {
    if (storeInfo) {
      const storeNumber = storeInfo.store;

      if (fulfillmentOption === 'bopl') {
        httpParams = httpParams.set('bopl', `${true}`).set('store', `${storeNumber}`);
      } else if (fulfillmentOption === 'bopis') {
        httpParams = httpParams.set('bopis', `${true}`).set('store', `${storeNumber}`);
      }
    }

    if (zipCode && fulfillmentOption === 'sameDayDelivery') {
      httpParams = httpParams.set('sdd', `${true}`).set('zipcode', `${zipCode}`);
    }

    return httpParams;
  }

  /**
   * @description
   * Sets personalization data params for golf balls on the HTTP params
   */
  public setPersonalizationData(
    personalizedData: PersonalizationData,
    httpParams: HttpParams,
  ): HttpParams {
    // Default type to TEXT only
    let personalizedType = 'golfball-text';

    // type NUMBER only
    if (personalizedData.hasNumberLine && !personalizedData.hasTextLine) {
      if (personalizedData.numberLineCharLimit === 1) {
        // Update the type
        personalizedType = 'golfball-single-number';
      } else if (personalizedData.numberLineCharLimit === 2) {
        // Update the type
        personalizedType = 'golfball-double-number';
      }
      // type TEXT & NUMBER
    } else if (personalizedData.hasNumberLine && personalizedData.hasTextLine) {
      // Update the type
      personalizedType = 'golfball-text-number';
    }

    // Number line
    if (personalizedData.hasNumberLine) {
      // Set param(s)
      httpParams = httpParams.set(
        'number',
        `${personalizedData.x_personalizationNumberLine1.trim()}`,
      );
    }

    // Text lines
    if (personalizedData.hasTextLine) {
      // make text lines array
      const textLines = this.createTextLinesArray(personalizedData);

      // Check to see if there are any text lines
      if (Array.isArray(textLines) && textLines.length > 0) {
        // Iterate the text lines
        textLines.forEach((textLine: string) => {
          // Set param(s)
          httpParams = httpParams.append('text', textLine);
        });
      }
    }

    // type LINEAID
    if (personalizedData.hasLineAid) {
      // Update the type
      personalizedType = 'golfball-lineaid';

      // Set param(s)
      httpParams = httpParams.set('style', `${this.getPersonalizationStyle(personalizedData)}`);
    }

    // Set TYPE param(s)
    httpParams = httpParams.set('personalized', personalizedType);

    return httpParams;
  }

  /**
   * @description
   * Creates an array of text lines from personalization data
   */
  public createTextLinesArray(personalizedData: PersonalizationData): string[] {
    const textLines = [];

    // Text line 1
    if (
      typeof personalizedData.x_personalizationTextLine1 === 'string' &&
      personalizedData.x_personalizationTextLine1.trim() !== ''
    ) {
      // Add text line to Array
      textLines.push(`${personalizedData.x_personalizationTextLine1.trim()}`);
    }

    // Text line 2
    if (
      typeof personalizedData.x_personalizationTextLine2 === 'string' &&
      personalizedData.x_personalizationTextLine2.trim() !== ''
    ) {
      // Add text line to Array
      textLines.push(`${personalizedData.x_personalizationTextLine2.trim()}`);
    }

    // Text line 3
    if (
      typeof personalizedData.x_personalizationTextLine3 === 'string' &&
      personalizedData.x_personalizationTextLine3.trim() !== ''
    ) {
      // Add text line to Array
      textLines.push(`${personalizedData.x_personalizationTextLine3.trim()}`);
    }

    return textLines;
  }

  /**
   * @description
   * Gets the personalization line style value for golf balls
   */
  public getPersonalizationStyle(personalizedData: PersonalizationData): string {
    // Default line style parameter value
    let styleValue = 'none';

    // Check the line style
    if (personalizedData.x_personalizationLineStyle === PersonalizedLineAidTextString.STRAIGHT) {
      // Update line style for StraightLine
      styleValue = 'straight';
    } else if (
      personalizedData.x_personalizationLineStyle === PersonalizedLineAidTextString.TARGET
    ) {
      // Update line style for TargetLine
      styleValue = 'target';
    }

    return styleValue;
  }
}
